import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, ButtonGroup, Grid, SelectChangeEvent
} from '@mui/material';
import Heatmap from './Heatmap';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:17001';

const DAYS = 30; // Default to 30 days

// Types
interface Machine {
  id: string;
  machine_id: string;
  capacity: number;
  remainingLife: number;
  costMaintenance: number;
  maintenanceLoss: number;
  costRepair: number;
  repairLoss: number;
  lossPerLifeDay: number;
}

interface HeatmapData {
  name: string;
  data: Array<{
    x: string;
    y: number;
  }>;
}

const generateHeatmapData = (machines: Machine[], days: number, valueGenerator: (machine: Machine, day: number) => number): HeatmapData[] => {
    return machines.map(machine => ({
      name: machine.machine_id || machine.id,
      data: Array.from({ length: days }, (_, day) => ({
        x: `Day-${String(day + 1).padStart(2, '0')}`,
        y: valueGenerator(machine, day),
      })),
    }));
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>{title}</Typography>
      {children}
    </Box>
  );
}

function Notes({ children }: { children: React.ReactNode }) {
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Section title="Notes">
            <Typography variant="body2">
                {children}
            </Typography>
        </Section>
      </Paper>
    );
}

function InputView() {
    // API-driven state
    const [toolTypes, setToolTypes] = useState<string[]>([]);
    const [toolsPerType, setToolsPerType] = useState<Record<string, string[]>>({});
    const [loadingToolTypes, setLoadingToolTypes] = useState(true);
    const [loadingTools, setLoadingTools] = useState(false);
    const [errorToolTypes, setErrorToolTypes] = useState<string | null>(null);
    
    // Selection state
    const [selectedToolType, setSelectedToolType] = useState<string>('');
    const [selectedTool, setSelectedTool] = useState<string>('');
    
    // Mode toggle
    const [mode, setMode] = useState<'mock' | 'live'>('mock');
    
    // Machine data state
    const [machines, setMachines] = useState<Machine[]>([]);
    
    // Calculation state
    const [plannedProduction, setPlannedProduction] = useState<HeatmapData[]>([]);
    const [predictedFailure, setPredictedFailure] = useState<HeatmapData[]>([]);
    const [cumulativeFailure, setCumulativeFailure] = useState<HeatmapData[]>([]);
    const [maintenanceCost, setMaintenanceCost] = useState<HeatmapData[]>([]);

    // Load tool types from API
    useEffect(() => {
        async function loadToolTypes() {
            setLoadingToolTypes(true);
            setErrorToolTypes(null);
            try {
                const response = await fetch(`${API_BASE}/api/tool-types?mode=${mode}`);
                const apiToolTypes = await response.json();
                setToolTypes(apiToolTypes);
                if (apiToolTypes.length > 0) {
                    setSelectedToolType(apiToolTypes[0]);
                }
            } catch (error) {
                console.error('Error loading tool types:', error);
                setErrorToolTypes('Error loading tool types');
            } finally {
                setLoadingToolTypes(false);
            }
        }
        loadToolTypes();
    }, [mode]);

    // Load tools for selected tool type
    useEffect(() => {
        async function loadTools() {
            if (!selectedToolType) return;
            
            setLoadingTools(true);
            try {
                const response = await fetch(`${API_BASE}/api/tools/${selectedToolType}?mode=${mode}`);
                const tools = await response.json();
                setToolsPerType(prev => ({
                    ...prev,
                    [selectedToolType]: tools
                }));
                if (tools.length > 0) {
                    setSelectedTool(tools[0]);
                }
            } catch (error) {
                console.error('Error loading tools for tool type:', error);
            } finally {
                setLoadingTools(false);
            }
        }
        loadTools();
    }, [selectedToolType, mode]);

    // Load machines for selected tool
    useEffect(() => {
        async function loadMachines() {
            if (!selectedTool) return;
            
            try {
                const response = await fetch(`${API_BASE}/api/modeling/machines?tool_name=${selectedTool}&mode=${mode}`);
                const machinesData = await response.json();
                // Transform API data to match expected format
                const transformedMachines: Machine[] = machinesData.map((machine: any) => ({
                    id: machine.machine_id,
                    machine_id: machine.machine_id,
                    capacity: machine.capacity || 100,
                    remainingLife: machine.remaining_life || 365,
                    costMaintenance: machine.maintenance_cost || 1000,
                    maintenanceLoss: machine.maintenance_loss || 500,
                    costRepair: machine.repair_cost || 2000,
                    repairLoss: machine.repair_loss || 1000,
                    lossPerLifeDay: machine.loss_per_day || 10
                }));
                setMachines(transformedMachines);
            } catch (error) {
                console.error('Error loading machines for tool:', error);
                setMachines([]);
            }
        }
        loadMachines();
    }, [selectedTool, mode]);

    const handleToolTypeChange = (event: SelectChangeEvent<string>) => {
        const newToolType = event.target.value;
        setSelectedToolType(newToolType);
        setSelectedTool('');
    };

    const handleToolChange = (event: SelectChangeEvent<string>) => {
        const newTool = event.target.value;
        setSelectedTool(newTool);
    };

    const handleMachineDataChange = (id: string, field: keyof Machine, value: string) => {
        const newMachines = machines.map(m => {
          if (m.id === id) {
            return { ...m, [field]: parseFloat(value) || 0 };
          }
          return m;
        });
        setMachines(newMachines);
    };

    const calculateAll = useCallback(() => {
        if (machines.length === 0) return;
        
        setPlannedProduction(generateHeatmapData(machines, DAYS, () => Math.floor(50 + Math.random() * 100)));
        setPredictedFailure(generateHeatmapData(machines, DAYS, (machine, day) => {
            const failureProb = Math.max(0, 1 - ((machine.remainingLife - day) / machine.remainingLife));
            return Math.min(1, Math.max(0, failureProb + (Math.random() - 0.5) * 0.2));
        }));
        setCumulativeFailure(generateHeatmapData(machines, DAYS, (machine, day) => {
            const dailyProbs = Array.from({length: day + 1}, (_, i) => {
                const p = Math.max(0, 1 - ((machine.remainingLife - i) / machine.remainingLife));
                return Math.min(1, Math.max(0, p + (Math.random() - 0.5) * 0.1));
            });
            const cumulativeProb = 1 - dailyProbs.reduce((acc, p) => acc * (1 - p), 1);
            return cumulativeProb;
        }));
        setMaintenanceCost(generateHeatmapData(machines, DAYS, (machine, day) => {
            return machine.costMaintenance + (day * machine.lossPerLifeDay);
        }));
    }, [machines]);

    // Initial calculation when machines change
    useEffect(() => {
        if (machines.length > 0) {
            calculateAll();
        }
    }, [machines, calculateAll]);

    // Loading state
    if (loadingToolTypes) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Error state
    if (errorToolTypes) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography color="error">{errorToolTypes}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            {/* Header with Mode Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Input Modeling</Typography>
                <ButtonGroup variant="outlined" size="small">
                    <Button
                        variant={mode === 'mock' ? 'contained' : 'outlined'}
                        onClick={() => setMode('mock')}
                    >
                        Mock Data
                    </Button>
                    <Button
                        variant={mode === 'live' ? 'contained' : 'outlined'}
                        onClick={() => setMode('live')}
                    >
                        Live Data
                    </Button>
                </ButtonGroup>
            </Box>

            <Notes>
                This view allows you to input and model semiconductor tool parameters for predictive maintenance and cost analysis.
            </Notes>

            {/* Tool Selection */}
            <Section title="Tool Selection">
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Tool Type</InputLabel>
                        <Select 
                            value={selectedToolType} 
                            label="Tool Type" 
                            onChange={handleToolTypeChange}
                            disabled={loadingToolTypes}
                        >
                            {(toolTypes || []).map(toolType => 
                                <MenuItem key={toolType} value={toolType}>{toolType}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Tool</InputLabel>
                        <Select 
                            value={selectedTool} 
                            label="Tool" 
                            onChange={handleToolChange}
                            disabled={loadingTools}
                        >
                            {(toolsPerType[selectedToolType] || []).map(tool => 
                                <MenuItem key={tool} value={tool}>{tool}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </Section>

            {/* Machine Data Input */}
            <Section title="Machine Parameters">
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Machine ID</TableCell>
                                <TableCell>Capacity</TableCell>
                                <TableCell>Remaining Life (days)</TableCell>
                                <TableCell>Maintenance Cost ($)</TableCell>
                                <TableCell>Maintenance Loss ($)</TableCell>
                                <TableCell>Repair Cost ($)</TableCell>
                                <TableCell>Repair Loss ($)</TableCell>
                                <TableCell>Loss per Day ($)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {machines.map((machine) => (
                                <TableRow key={machine.id}>
                                    <TableCell>{machine.machine_id}</TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={machine.capacity}
                                            onChange={(e) => handleMachineDataChange(machine.id, 'capacity', e.target.value)}
                                            type="number"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={machine.remainingLife}
                                            onChange={(e) => handleMachineDataChange(machine.id, 'remainingLife', e.target.value)}
                                            type="number"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={machine.costMaintenance}
                                            onChange={(e) => handleMachineDataChange(machine.id, 'costMaintenance', e.target.value)}
                                            type="number"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={machine.maintenanceLoss}
                                            onChange={(e) => handleMachineDataChange(machine.id, 'maintenanceLoss', e.target.value)}
                                            type="number"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={machine.costRepair}
                                            onChange={(e) => handleMachineDataChange(machine.id, 'costRepair', e.target.value)}
                                            type="number"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={machine.repairLoss}
                                            onChange={(e) => handleMachineDataChange(machine.id, 'repairLoss', e.target.value)}
                                            type="number"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={machine.lossPerLifeDay}
                                            onChange={(e) => handleMachineDataChange(machine.id, 'lossPerLifeDay', e.target.value)}
                                            type="number"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            {/* Calculate Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={calculateAll}
                    size="large"
                >
                    Calculate Models
                </Button>
            </Box>

            {/* Results */}
            {plannedProduction.length > 0 && (
                <Section title="Modeling Results">
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Planned Production</Typography>
                                <Heatmap data={plannedProduction} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Predicted Failure Probability</Typography>
                                <Heatmap data={predictedFailure} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Cumulative Failure Probability</Typography>
                                <Heatmap data={cumulativeFailure} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Maintenance Cost</Typography>
                                <Heatmap data={maintenanceCost} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Section>
            )}
        </Box>
    );
}

export default InputView; 